<script setup lang="ts">
import PdsButton from '@/components/pds/PdsButton.vue';
import PdsIcon from '@/components/pds/PdsIcon.vue';
import PdsModal from '@/components/pds/PdsModal.vue';
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import { formatDateTime } from '../format';
import { usePdsOrdersStore } from '../stores/orders.store';
import InfoNotice from './InfoNotice.vue';
import type { PdsOrder } from '../types';

const props = defineProps<{ order: PdsOrder }>();

const store = usePdsOrdersStore();
const missing = computed(() => props.order.items.filter((i) => !i.prepared));
</script>

<template>
  <PdsModal
    :size="missing.length ? 'xl' : 'sm'"
    @close="store.cancelReadyRequest()"
  >
    <template #title>Marquer prête</template>
    <template #subtitle>
      En préparation <PdsIcon name="arrow-right" :size="14" /> Prête
    </template>

    <div class="flex flex-col md:flex-row">
      <div class="flex min-w-0 flex-1 flex-col gap-4 p-5">
        <dl class="flex flex-col gap-5">
          <div class="flex items-baseline gap-[10px]">
            <dt
              class="text-encre-douce w-[95px] shrink-0 text-[12px] font-semibold tracking-[0.9px] uppercase"
            >
              Commande
            </dt>
            <dd class="text-[18px] leading-[1.2] font-bold">
              #{{ order.number }} · {{ order.label }}
            </dd>
          </div>
          <div class="flex items-baseline gap-[10px]">
            <dt
              class="text-encre-douce w-[95px] shrink-0 text-[12px] font-semibold tracking-[0.9px] uppercase"
            >
              Retrait
            </dt>
            <dd class="text-[18px] leading-[1.2] font-semibold">
              {{ formatDateTime(order.dueAt) }}
            </dd>
          </div>
          <div
            v-if="order.contact.phone"
            class="flex items-baseline gap-[10px]"
          >
            <dt
              class="text-encre-douce w-[95px] shrink-0 text-[12px] font-semibold tracking-[0.9px] uppercase"
            >
              Téléphone
            </dt>
            <dd class="text-[18px] leading-[1.2] font-semibold">
              {{ order.contact.phone }}
            </dd>
          </div>
        </dl>

        <p
          v-if="order.customerNote"
          class="bg-creux flex w-fit max-w-full items-center gap-2 rounded-[8px] px-[10px] py-[6px] text-[12px]"
        >
          <span
            class="text-encre-douce font-semibold tracking-[0.9px] uppercase"
          >
            Client
          </span>
          <span>{{ order.customerNote }}</span>
        </p>

        <InfoNotice v-if="order.contact.phone" title="Le client sera prévenu">
          Un SMS part dès la confirmation, avec le lien vers sa commande. Le
          détail n’y figure pas.
        </InfoNotice>
      </div>

      <aside
        v-if="missing.length"
        class="bg-papier flex w-full shrink-0 flex-col gap-3 p-5 md:w-[340px]"
      >
        <p class="text-[14px] leading-[1.4] font-semibold">
          {{ missing.length }} produit{{ missing.length > 1 ? 's' : '' }} ne
          {{ missing.length > 1 ? 'sont' : 'est' }} pas
          {{ missing.length > 1 ? 'préparés' : 'préparé' }}
        </p>
        <ul class="flex-1">
          <li
            v-for="item in missing"
            :key="item.id"
            class="border-filet flex gap-3 border-b px-3 py-3 text-[14px] leading-[1.4]"
          >
            <span class="min-w-[18px] shrink-0 font-semibold">
              {{ item.quantity }}<span class="text-[12px] font-normal">×</span>
            </span>
            {{ item.name }}
          </li>
        </ul>
        <RouterLink
          :to="`/commande/${order.id}/modifier`"
          class="bg-second text-second-encre flex h-[62px] items-center justify-center rounded-[12px] text-[14px] leading-[1.4] font-semibold"
          @click="store.cancelReadyRequest()"
        >
          Modifier la commande
        </RouterLink>
      </aside>
    </div>

    <template #footer>
      <PdsButton variant="second" fit @click="store.cancelReadyRequest()">
        Retour
      </PdsButton>
      <span class="hidden flex-1 sm:block"></span>
      <PdsButton fit @click="store.confirmReady()">
        Confirmer · marquer prête
      </PdsButton>
    </template>
  </PdsModal>
</template>
